const STORAGE_ORG_ID_KEY = "twido.auth0.organization";
const STORAGE_LOGIN_NAME_KEY = "twido.auth0.organization_login";
const MISSING_ORG_RETRY_KEY = "twido.auth0.missing-org-retry";

/**
 * Legacy default tenant (`fcs-gen`). Not used for login redirects — kept for
 * tests and documentation of the Tideway org id.
 */
export const DEFAULT_AUTH0_ORGANIZATION = "org_2fPcPsWZUogS28PF";

/** Auth0 organization IDs are `org_` plus an alphanumeric suffix. */
export function isAuth0OrgId(value: string): boolean {
  return /^org_[A-Za-z0-9]+$/.test(value);
}

/** Auth0 organization slug (`name`) — lowercase letters, digits, hyphen. */
export function isAuth0OrganizationName(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 1 || trimmed.length > 255) return false;
  return /^[a-z0-9-]+$/.test(trimmed);
}

/** Value accepted on `/authorize` as the `organization` parameter (id or slug). */
export function isAuth0OrganizationLoginValue(value: string): boolean {
  const trimmed = value.trim();
  return isAuth0OrgId(trimmed) || isAuth0OrganizationName(trimmed);
}

export function normalizeOrganizationForAuthorize(value: string): string {
  const trimmed = value.trim();
  if (isAuth0OrgId(trimmed)) return trimmed;
  return trimmed.toLowerCase();
}

/** Persist Auth0 org id from the token (used for silent token refresh). */
export function persistAuth0Organization(orgId: string): void {
  if (!isAuth0OrgId(orgId)) return;
  try {
    localStorage.setItem(STORAGE_ORG_ID_KEY, orgId);
  } catch {
    // Private mode / quota — silent refresh may re-prompt login.
  }
}

/** Remember the org slug or id the user chose on the login screen. */
export function persistOrganizationLoginChoice(value: string): void {
  if (!isAuth0OrganizationLoginValue(value)) return;
  const normalized = normalizeOrganizationForAuthorize(value);
  try {
    if (isAuth0OrgId(normalized)) {
      localStorage.setItem(STORAGE_ORG_ID_KEY, normalized);
    } else {
      localStorage.setItem(STORAGE_LOGIN_NAME_KEY, normalized);
    }
  } catch {
    // ignore
  }
}

export function readStoredAuth0Organization(): string | undefined {
  try {
    const value = localStorage.getItem(STORAGE_ORG_ID_KEY);
    return value && isAuth0OrgId(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function readStoredOrganizationLoginName(): string | undefined {
  try {
    const value = localStorage.getItem(STORAGE_LOGIN_NAME_KEY);
    return value && isAuth0OrganizationName(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Auth0 Organizations put `org_id` on the ID token. The SPA SDK also keeps
 * that claim in its `@@auth0spajs@@` cache, which we can read after a failed
 * callback when `useAuth0().user` is not yet available.
 */
export function readAuth0OrganizationFromSdkCache(): string | undefined {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith("@@auth0spajs@@")) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as {
        body?: {
          decodedToken?: {
            claims?: { org_id?: unknown };
            user?: { org_id?: unknown };
          };
        };
      };
      const claims = parsed.body?.decodedToken;
      const fromClaims = claims?.claims?.org_id;
      const fromUser = claims?.user?.org_id;
      if (typeof fromClaims === "string" && isAuth0OrgId(fromClaims)) {
        return fromClaims;
      }
      if (typeof fromUser === "string" && isAuth0OrgId(fromUser)) {
        return fromUser;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function auth0UserOrganizationId(user?: unknown): string | undefined {
  if (!user || typeof user !== "object") return undefined;
  const orgId = (user as { org_id?: unknown }).org_id;
  return typeof orgId === "string" && isAuth0OrgId(orgId) ? orgId : undefined;
}

/** Optional dev default from `VITE_AUTH0_ORGANIZATION` (id or slug). */
export function readOrganizationFromEnv(): string | undefined {
  const fromEnv = import.meta.env.VITE_AUTH0_ORGANIZATION;
  if (!fromEnv || typeof fromEnv !== "string") return undefined;
  const trimmed = fromEnv.trim();
  if (isAuth0OrganizationLoginValue(trimmed)) {
    return normalizeOrganizationForAuthorize(trimmed);
  }
  return undefined;
}

/**
 * Organization for silent token refresh and authenticated API calls — never
 * guesses a tenant; uses the signed-in user's org, storage, or SDK cache.
 */
export function resolveAuth0Organization(
  override?: string | null,
): string | undefined {
  if (override && isAuth0OrganizationLoginValue(override)) {
    return normalizeOrganizationForAuthorize(override);
  }
  return (
    readOrganizationFromEnv() ??
    readStoredAuth0Organization() ??
    readAuth0OrganizationFromSdkCache()
  );
}

/**
 * Organization to send on `/authorize` for org switch (and dev overrides).
 * Normal sign-in omits `organization` so Auth0's org prompt runs.
 */
export function resolveAuth0OrganizationForAuthorize(
  override?: string | null,
): string | undefined {
  if (override && isAuth0OrganizationLoginValue(override)) {
    return normalizeOrganizationForAuthorize(override);
  }
  return readOrganizationFromEnv();
}

export function isMissingOrganizationError(
  errorCode: string | null | undefined,
  errorDescription: string | null | undefined,
): boolean {
  return (
    errorCode === "invalid_request" &&
    (errorDescription ?? "").toLowerCase().includes("organization")
  );
}

export function consumeMissingOrganizationRetry(): boolean {
  try {
    if (sessionStorage.getItem(MISSING_ORG_RETRY_KEY) === "1") return false;
    sessionStorage.setItem(MISSING_ORG_RETRY_KEY, "1");
    return true;
  } catch {
    return true;
  }
}

export function clearMissingOrganizationRetry(): void {
  try {
    sessionStorage.removeItem(MISSING_ORG_RETRY_KEY);
  } catch {
    // ignore
  }
}

/** Persist org id when known, then return the session org for token calls. */
export function rememberAuth0Organization(
  override?: string | null,
): string | undefined {
  const organization = resolveAuth0Organization(override);
  if (organization && isAuth0OrgId(organization)) {
    persistAuth0Organization(organization);
  }
  return organization;
}
